import $ from 'jquery';
import debounce from 'just-debounce';
import CurrencyConverter from 'shopify-currency-converter';

import MessageBanner from './MessageBanner';
import getHeaderOffset from '../helpers/getHeaderOffset';
import Images from '../helpers/Images';

class AddToCartFlyout {
  constructor() {
    this.formData = {};
    this.settings = {};
    this.events = [];
    this.Images = new Images();
    this.flyOutSelector = '[data-atc-banner]';
    this.$atcTemplate = $(`[data-templates] ${this.flyOutSelector}`);

    this._onInit = this._onInit.bind(this);
    this._onOpen = this._onOpen.bind(this);
    this._onClose = this._onClose.bind(this);
    this._onError = this._onError.bind(this);
    this._onSuccess = this._onSuccess.bind(this);
    this._handleDocumentClick = this._handleDocumentClick.bind(this);

    this.messageBanner = new MessageBanner();

    this.header = document.querySelector('.site-header-wrapper');
    this.$header = $(this.header);
    this.$body = $(document.body);
  }

  init(formData, options, callbacks = {}) {
    // If any FlyOuts are fully open, close them
    this._closeFlyOuts();

    this.formData = formData;

    // Allows ATC Flow to be overridden
    this.callbacks = $.extend({
      onInit: this._onInit,
      onClose: this._onClose,
      onError: this._onError,
      onOpen: this._onOpen,
      onSuccess: this._onSuccess,
    }, callbacks);

    const $atcButton = options.$atcButton;

    this.settings = $.extend({
      moneyFormat: null,
      cartRedirection: false,
    }, options.settings);

    this.callbacks.onInit();

    this._updateCart($atcButton);
    this._disableButton($atcButton);
  }

  unload() {
    this.messageBanner.dismissBanners();
    this._removeFlyOuts();
    delete this.Images;
  }

  _updateCart($atcButton) {
    const $flyOut = this.$atcTemplate.clone();

    const quantityField = this.formData.filter(data => { return data.name === 'quantity'; });
    const quantity = quantityField[0].value;

    $.ajax({
      type: 'POST',
      url: '/cart/add.js',
      data: $.param(this.formData),
      dataType: 'json',
    })
    .done((response) => {
      if (response.image) {
        const imageUrl = this.Images.getSizedImageUrl(response.image, '200x');
        const image = this.Images.loadImage(imageUrl);

        $flyOut.find('[data-atc-banner-product-image]')
          .html(`<img src="${image}" alt="${response.product_title}">`);
      }

      $flyOut.find('[data-atc-banner-product-title]')
        .text(`${response.product_title} × ${quantity}`);

      /*
        TODO: Bring in `variant.options`, iterate through to get option
          name for: <strong>Option name:</strong> Option
       */
      if (response.variant_options[0] !== 'Title' && response.variant_options[0] !== 'Default Title') {
        $flyOut.find('[data-atc-banner-product-options]')
          .text(response.variant_options.join(', '));
      }

      /*
        TODO: Bring in variant, and use that to check compare_at_price
          to see if the item is on sale
       */
      const $productPrice = $flyOut.find('[data-atc-banner-product-price]');
      $productPrice.text(Shopify.formatMoney(response.price, this.settings.moneyFormat));

      // Update price to local currency
      CurrencyConverter.update($productPrice[0]);

      $.ajax({
        type: 'GET',
        url: '/cart.js',
        dataType: 'json',
      })
      .done((response) => {
        this.callbacks.onSuccess();

        // Reset formData in case instance is never cleared
        this.formData = {};

        if (this.settings.cartRedirection) {
          return location.href = '/cart';
        }

        const $subTotal = $flyOut.find('[data-atc-banner-cart-subtotal]');
        $subTotal.text(Shopify.formatMoney(response.original_total_price, this.settings.moneyFormat));
        CurrencyConverter.update($subTotal[0]);

        $flyOut
          .find('[data-atc-banner-cart-button]')
          .find('span').text(response.item_count);

        this.$body.append($flyOut);

        // Notifiy Header of new cart count
        this.$header.trigger('cartcount:update', { response });

        this._positionFlyOut($flyOut);
        this._bindEvents($flyOut);
        this._openFlyOut($flyOut, $atcButton);
      });
    })
    .fail((response) => {
      const responseText = JSON.parse(response.responseText);
      const error = responseText.description;

      this._enableButton($atcButton);
      this.callbacks.onError(error);
    });
  }

  _onError(error) {
    this.messageBanner.message(error, 'error');
  }

  _onInit() {
    this.messageBanner.dismissBanners();
  }

  _onOpen() {
    /*
      The `this.callbacks.onOpen` is used pull keyboard focus into the active flyout
     */
    console.warn('AddToCartFlyout onOpen callback not specified');
  }

  _onClose() {
    /*
      The `this.callbacks.onClose` is used to return focus to originating DOM
     */

    console.warn('AddToCartFlyout onClose callback not specified');
  }

  _onSuccess() {
    /*
      By default, the ATC Flyout doesn't need any additional success callbacks

      The `this.callbacks.onSuccess` is used to allow other views to initiate
      behaviour when a product has been added to the cart
     */
  }

  _bindEvents($flyOut) {
    this.events.push([
      $flyOut.on('click.atc-banner', '[data-atc-banner-close]', (event) => {
        event.preventDefault();
        this._closeFlyOut($flyOut);
      }),
      this.$body.on('position-banners.atc-banner', () => {
        this._positionFlyOut($flyOut);
      }),
      $(window).on('resize.atc-banner', () => {
        debounce(this._positionFlyOut($flyOut), 250);
      }),
      $(window).on('scroll.atc-banner', () => {
        this._positionFlyOut($flyOut);
      }),
      $(document).on('touchstart.atc-banner, click.atc-banner', this._handleDocumentClick),
    ]);
  }

  /**
   * Trigger any existing open FlyOuts to close
   *
   * @private
   */
  _closeFlyOuts($excludeFlyOut = null) {
    $(this.flyOutSelector).filter('[data-flyout-active]').each((index, el) => {
      const $flyOut = $(el);

      if ($excludeFlyOut && $flyOut.is($excludeFlyOut)) {
        return;
      }

      this._closeFlyOut($flyOut, index);
    });
  }

  _openFlyOut($flyOut, $atcButton) {
    /*
     If user has initiated a new ATC Flow before the first has finished,
     the first FlyOut could have opened after the first attempt to close open flyouts
    */
    this._closeFlyOuts($flyOut);

    $flyOut
      .addClass('animating animating-in')
      .one('trend', () => {
        $flyOut
          .removeClass('animating animating-in')
          .addClass('visible')
          .off('trend');

        $flyOut.attr('data-flyout-active', true);
        this.callbacks.onOpen({
          $container: $flyOut,
          $elementToFocus: $flyOut.find('[data-atc-banner-cart-button]')
        });

        this._enableButton($atcButton);
      });
  }

  /**
   * Close an open FlyOut
   *
   * @param $flyOut
   * @param index
   * @private
   */
  _closeFlyOut($flyOut = null, index = 0) {
    if (!$flyOut) {
      return;
    }

    $flyOut
      .addClass('animating animating-out')
      .one('trend', () => {
        $flyOut
          .removeClass('animating animating-out visible')
          .one('trend', () => {
            $flyOut.off('trend');
            this._removeFlyOut($flyOut, index);
            this.callbacks.onClose();
          });
      });
  }

  _removeFlyOuts() {
    $(this.flyOutSelector).filter('[data-flyout-active]').each((index, el) => {
      this._removeFlyOut($(el), index);
    });
  }

  /**
   * Remove the Flyout from the DOM, and clean up listeners
   *
   * @param $flyOut
   * @param index
   * @private
   */
  _removeFlyOut($flyOut, index) {
    if (this.events[index]) {
      this.events[index].forEach($el => $el.off('.atc-banner'));
      this.events.splice(index, 1);
    }

    if ($flyOut) {
      $flyOut.remove();
    }
  }

  /**
   * Position FlyOut beneath header if its in viewport, or at top of viewport
   *
   * @param $flyOut
   * @private
   */
  _positionFlyOut($flyOut = null) {
    $flyOut.css({ top: getHeaderOffset(this.header) + parseInt(this.header.style.top, 10) });
  }

  _disableButton($atcButton) {
    $atcButton
      .addClass('processing')
      .prop('disabled', true);
  }

  _enableButton($atcButton) {
    $atcButton
      .prop('disabled', false)
      .removeClass('processing');
  }

  _handleDocumentClick(event) {
    const target = event.target;
    const $parent = $(target).parents('[data-atc-banner]');

    if ($parent.length) {
      return;
    }

    this._closeFlyOuts();
  }
}

export default new AddToCartFlyout();
